import React, { useState, useRef, useEffect, useCallback } from "react";
import { Avatar, Box, Stack, Typography, Divider, Menu, MenuItem, ListItemIcon, Tooltip } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import PersonIcon from "@mui/icons-material/Person";
import LanguageIcon from "@mui/icons-material/Language";
import LogoutIcon from "@mui/icons-material/Logout";
import DvrIcon from "@mui/icons-material/Dvr";
import NotificationBell from "./NotificationBell";
import MilestoneCelebration from "../../components/MilestoneCelebration";
import LeadershipAPI from "../../api/LeadershipAPI";

const DEFAULT_CELEBRATION_MS = 30000;

const HeaderProfile = () => {
	// All hooks must be called before any conditional returns
	const [isMounted, setIsMounted] = useState(false);
	const [anchorEl, setAnchorEl] = useState(null);
	const triggerRef = useRef(null);
	// Server-computed: the shortcode only fills this for a field agent holding a
	// milestone they have not been shown yet, so an empty list is the norm.
	const [celebration, setCelebration] = useState({ milestones: [], duration: DEFAULT_CELEBRATION_MS });

	useEffect(() => {
		setIsMounted(true);

		const pending = window?.ahgProfile?.milestone_celebration;
		if (Array.isArray(pending?.milestones) && pending.milestones.length > 0) {
			setCelebration({
				milestones: pending.milestones,
				duration: Number(pending.duration) || DEFAULT_CELEBRATION_MS,
			});
		}

		return () => setIsMounted(false);
	}, []);

	// Latched as each card is shown rather than on dismiss, so a refresh mid-run
	// resumes at the card the agent had not reached instead of replaying from the
	// start. The endpoint merges, so a repeat call is harmless.
	const handleMilestoneShown = useCallback((milestone) => {
		LeadershipAPI.acknowledgeMilestoneCelebration(milestone.target).catch((error) =>
			console.warn("Could not acknowledge milestone celebration:", error)
		);
	}, []);

	// Early return AFTER all hooks are called
	if (!isMounted) {
		return <div style={{ display: "none" }}>Loading...</div>;
	}

	// Get the user data from the localized WordPress data with error handling
	let userData;
	try {
		userData = window?.ahgProfile?.user_data || {
			name: "User",
			last_name: "",
			roles: [],
		};
	} catch (error) {
		console.warn("Error accessing user data:", error);
		userData = {
			name: "User",
			last_name: "",
			roles: [],
		};
	}

	localStorage.setItem("loggedin_data", JSON.stringify(ahgProfile.user_data));

	const fullName = `${userData.name} ${userData.last_name}`.trim();
	const userRole = userData.roles[0] || "User";
	const userInitials = `${userData.name?.[0] || ""}${userData.last_name?.[0] || ""}`.toUpperCase();

	const open = Boolean(anchorEl);

	const handleMenuToggle = (event) => {
		try {
			if (open) {
				setAnchorEl(null);
			} else {
				setAnchorEl(event.currentTarget);
			}
		} catch (error) {
			console.warn("Error toggling menu:", error);
			setAnchorEl(null);
		}
	};

	const handleMenuClose = () => {
		try {
			setAnchorEl(null);
		} catch (error) {
			console.warn("Error closing menu:", error);
		}
	};

	const handleProfile = () => {
		window.location.href = "/settings";
		handleMenuClose();
	};

	const handlePortal = () => {
		// Handle portal navigation
		window.location.href = "/client-list-2";
		handleMenuClose();
	};

	const handleTimezone = () => {
		handleMenuClose();
	};

	const handleLogout = async () => {
		try {
			handleMenuClose();
			const nonce = window?.ahgProfile?.nonce;

			const response = await fetch(`${window.location.origin}/wp-json/ahg/v1/logout`, {
				method: "POST",
				credentials: "same-origin",
				headers: {
					"Content-Type": "application/json",
					"X-WP-Nonce": nonce,
				},
			});

			if (response.ok) {
				const data = await response.json();
				window.location.href = data.redirect_url || "/";
			} else {
				console.error("Logout failed:", response.statusText);
				window.location.href = "/wp-login.php?action=logout&redirect_to=/";
			}
		} catch (error) {
			console.error("Error during logout:", error);
			window.location.href = "/wp-login.php?action=logout&redirect_to=/";
		}
	};

	return (
		<>
			<Stack direction="row" spacing={1} alignItems="center !important" sx={{ mt: "2px", pt: "5px" }}>
				{/* Notification Bell - positioned before avatar */}
				<NotificationBell />

				<Divider
					orientation="vertical"
					flexItem
					sx={{
						borderColor: "#A9A9A9",
						borderWidth: 1.2,
						ml: 3,
						height: "auto",
						minHeight: "40px",
					}}
				/>

				{/* Profile Trigger */}
				<Box
					ref={triggerRef}
					onClick={handleMenuToggle}
					sx={{
						display: "flex",
						alignItems: "center",
						cursor: "pointer",
						ml: "12px !important",
						px: 0.5,
						py: 0.5,
						borderRadius: 1,
						"&:hover": {
							backgroundColor: "#f5f5f5",
						},
					}}
				>
					{/* Avatar */}
					<Box
						sx={{
							border: `2px solid #1D54A2`,
							borderRadius: "50%",
							padding: "1px",
							display: "inline-block",
							boxSizing: "border-box",
							mr: 1,
						}}
					>
						<Avatar
							src={userData?.avatar || `https://api.dicebear.com/7.x/initials/png?seed=${encodeURIComponent(userInitials)}`}
							alt={fullName}
							sx={{ height: 34, width: 34, backgroundColor: "#fff" }}
						/>
					</Box>
					{/* User Info Header */}
					<Box sx={{ display: "flex", alignItems: "center", mr: 1.2 }}>
						<Box>
							<Typography variant="body2" sx={{ fontWeight: 600, color: "#333" }}>
								{fullName}
							</Typography>
							<Typography variant="caption" sx={{ color: "#666" }}>
								{userRole}
							</Typography>
						</Box>
					</Box>

					{open ? (
						<ExpandLessIcon sx={{ stroke: "#1D54A2", strokeWidth: 0.5, fontSize: "26px" }} />
					) : (
						<ExpandMoreIcon sx={{ stroke: "#1D54A2", strokeWidth: 0.5, fontSize: "26px" }} />
					)}
				</Box>

				{/* Dropdown Menu */}
				<Menu
					anchorEl={anchorEl}
					open={open}
					onClose={handleMenuClose}
					anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
					transformOrigin={{ vertical: "top", horizontal: "right" }}
					disableScrollLock
					slotProps={{
						paper: {
							sx: {
								mt: 1.2,
								boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.1)",
								borderRadius: 3,
								minWidth: 220,
								backgroundColor: "#ffffff",
							},
						},
					}}
				>
					<Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid #e0e0e0" }}>
						<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
							<Box>
								<Typography variant="body1" sx={{ fontWeight: 600, color: "#1D54A2" }}>
									{fullName}
								</Typography>
								<Typography variant="caption" sx={{ color: "#666" }}>
									{userRole}
								</Typography>
							</Box>
						</Box>
					</Box>
					{/* Menu Items */}
					<MenuItem onClick={handlePortal} sx={{ py: 1.5, px: 2 }}>
						<ListItemIcon sx={{ minWidth: 36 }}>
							<DvrIcon sx={{ fontSize: 20, color: "#1D54A2" }} />
						</ListItemIcon>
						<Typography variant="body2">Portal</Typography>
					</MenuItem>
					<MenuItem onClick={handleProfile} sx={{ py: 1.5, px: 2 }}>
						<ListItemIcon sx={{ minWidth: 36 }}>
							<PersonIcon sx={{ fontSize: 20, color: "#1D54A2" }} />
						</ListItemIcon>
						<Typography variant="body2">Profile</Typography>
					</MenuItem>

					<Divider sx={{ my: 0.5 }} />

					<MenuItem onClick={handleLogout} sx={{ py: 1.5, px: 2 }}>
						<ListItemIcon sx={{ minWidth: 36 }}>
							<LogoutIcon sx={{ fontSize: 20, color: "#1D54A2" }} />
						</ListItemIcon>
						<Typography variant="body2">Logout</Typography>
					</MenuItem>
				</Menu>
			</Stack>

			{celebration.milestones.length > 0 && (
				<MilestoneCelebration
					open
					onClose={() => setCelebration((c) => ({ ...c, milestones: [] }))}
					onMilestoneShown={handleMilestoneShown}
					agentName={fullName}
					avatarUrl={userData?.avatar}
					milestones={celebration.milestones}
					autoCloseMs={celebration.duration}
				/>
			)}
		</>
	);
};

export default HeaderProfile;
